import type { AppNotification, NotificationRepository, NotificationEvent, NotificationTarget, NotificationType } from "../../domain/notification/notification.js";
import { InMemoryNotificationRepository } from "../../infrastructure/database/memory/in-memory-notification-repository.js";

// Optional: you can extract this to a DI container later if needed.
const defaultRepo = new InMemoryNotificationRepository();

export class NotificationService {
  /**
   * Filtro de CONFIGURAÇÕES → NOTIFICAÇÕES. Sem filtro ligado (testes,
   * composições que não o fornecem), tudo é notificado — o comportamento de
   * sempre. O filtro só desliga grupos de evento; alerta do sistema não passa
   * por ele.
   */
  private eventFilter: ((event: NotificationEvent) => boolean) | null = null;

  constructor(private readonly repo: NotificationRepository = defaultRepo) {}

  attachEventFilter(filter: (event: NotificationEvent) => boolean): void {
    this.eventFilter = filter;
  }

  public async getNotifications(userId: string): Promise<AppNotification[]> {
    if (!userId) return [];
    return this.repo.findByUserId(userId);
  }

  public async getUnreadCount(userId: string): Promise<number> {
    if (!userId) return 0;
    return this.repo.getUnreadCount(userId);
  }

  public async markAsRead(id: string, userId?: string): Promise<void> {
    await this.repo.markAsRead(id);
    this.dispatchUpdateEvent();
  }

  public async markAllAsRead(userId: string): Promise<void> {
    if (!userId) return;
    await this.repo.markAllAsRead(userId);
    this.dispatchUpdateEvent();
  }

  public async createNotification(data: {
    userId: string;
    type: NotificationType;
    event: NotificationEvent;
    title: string;
    message: string;
    entityType?: AppNotification['entityType'];
    entityId?: string;
    clientId?: string;
    projectId?: string;
    route?: string;
    target?: NotificationTarget;
    idempotencyKey?: string;
  }): Promise<AppNotification | null> {
    if (data.event !== 'SYSTEM_ALERT' && this.eventFilter && !this.eventFilter(data.event)) return null;
    if (data.idempotencyKey) {
      const existing = await this.repo.findByIdempotencyKey(data.idempotencyKey);
      if (existing) return existing; // Ignore duplicate
    }

    const notification: AppNotification = {
      id: `notif-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`,
      userId: data.userId,
      type: data.type,
      event: data.event,
      title: data.title,
      message: data.message,
      ...(data.entityType ? { entityType: data.entityType } : {}),
      ...(data.entityId ? { entityId: data.entityId } : {}),
      ...(data.clientId ? { clientId: data.clientId } : {}),
      ...(data.projectId ? { projectId: data.projectId } : {}),
      ...(data.route ? { route: data.route } : {}),
      ...(data.target ? { target: data.target } : {}),
      createdAt: new Date().toISOString(),
      readAt: null,
      isRead: false,
      createdBySystem: true,
      ...(data.idempotencyKey ? { idempotencyKey: data.idempotencyKey } : {})
    };

    await this.repo.save(notification);
    this.dispatchUpdateEvent();
    return notification;
  }

  private dispatchUpdateEvent(): void {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    }
  }
}

export const notificationService = new NotificationService();
